import { useMemo } from "react";

import { Field, FieldArray, FormikErrors, useFormikContext } from "formik";

import {
  AddAttachmentsForm,
  AddAttachmentsLink,
} from "manager/components/Form/AddAttachmentsForm";
import ErrorList from "shared/components/ErrorList";
import { Checkbox, TextArea } from "shared/components/Form";
import { useActiveCompany } from "shared/hooks";

import { Attachments } from "../styled";

import IndividualEmailPreview from "./IndividualEmailPreview";
import { Form } from "./types";

type Props = {
  index: number;
  previewEmail?: (
    applicantId: number,
    includeAdverseAction?: boolean,
    formData?: Form["applicants"][0]
  ) => Promise<any>;
  applicantData?: { id: number; firstName: string; lastName: string };
  isSelected?: boolean;
};

const ConditionallyApproveForm = ({
  index,
  previewEmail,
  applicantData,
  isSelected,
}: Props) => {
  const { values, errors, setFieldValue } = useFormikContext<Form>();
  const { activeCompany } = useActiveCompany();
  const ingestionFlowEnabled = activeCompany?.ingestionFlowEnabled;

  const formData = values?.applicants?.[index];
  const attachmentsEnabled = formData?.requiresAdditionalAttachment;
  const messageEnabled = formData?.readMessage;
  const formErrors = errors?.applicants?.[index] as FormikErrors<
    Form["applicants"][0]
  >;

  const guarantorId = useMemo(() => {
    if (formData?.requiresAdditionalGuarantor) {
      return 1;
    }
    return null;
  }, [formData?.requiresAdditionalGuarantor]);

  const attachmentIds = useMemo(() => {
    if (formData?.additionalAttachments) {
      return formData.additionalAttachments.map((_, idx) => idx);
    }
    return [];
  }, [formData?.additionalAttachments]);

  // Wrap the previewEmail function to include current form data
  const previewEmailWithFormData = useMemo(() => {
    if (!previewEmail) return undefined;

    return (applicantId: number, includeAdverseAction?: boolean) => {
      return previewEmail(applicantId, includeAdverseAction, formData);
    };
  }, [previewEmail, formData]);

  return (
    <>
      {/* @ts-ignore */}
      {formErrors?.nonFieldErrors && (
        // @ts-ignore
        <ErrorList errors={formErrors.nonFieldErrors} />
      )}

      <p>What do they need to do?</p>
      {!ingestionFlowEnabled && (
        <Field
          id={`applicants.${index}.requiresAdditionalGuarantor`}
          name={`applicants.${index}.requiresAdditionalGuarantor`}
          label="Add a guarantor"
          as={Checkbox}
          error={formErrors?.requiresAdditionalGuarantor}
        />
      )}
      <br />
      <Field
        id={`applicants.${index}.requiresAdditionalAttachment`}
        name={`applicants.${index}.requiresAdditionalAttachment`}
        label="Add additional attachments"
        as={Checkbox}
        error={formErrors?.requiresAdditionalAttachment}
      />
      <br />
      <Attachments>
        <FieldArray
          name={`applicants.${index}.additionalAttachments`}
          render={({ push, remove }) => (
            <>
              <AddAttachmentsForm
                remove={remove}
                disabled={!attachmentsEnabled}
                index={index}
                name={`applicants.${index}.additionalAttachments`}
                errors={formErrors?.additionalAttachments}
              />
              {attachmentsEnabled && (
                <AddAttachmentsLink addAttachment={push} />
              )}
            </>
          )}
        />
      </Attachments>
      <br />
      <Field
        as={Checkbox}
        name={`applicants.${index}.readMessage`}
        id={`applicants.${index}.readMessage`}
        label="Read the message below"
        error={formErrors?.readMessage}
      />
      <br />
      <Field
        as={TextArea}
        id={`applicants.${index}.customMessage`}
        name={`applicants.${index}.customMessage`}
        label="Your message"
        rows={5}
        error={formErrors?.customMessage}
        disabled={!messageEnabled}
      />

      {previewEmail && applicantData && (
        <IndividualEmailPreview
          applicantId={applicantData.id}
          applicantName={`${applicantData.firstName} ${applicantData.lastName}`}
          previewEmail={previewEmailWithFormData}
          isSelected={isSelected || false}
          guarantorId={guarantorId}
          attachmentIds={attachmentIds}
          customMessage={formData?.customMessage}
          excludeAdverseActionInEmail={formData?.excludeAdverseActionInEmail}
          setFieldValue={setFieldValue}
          index={index}
        />
      )}
    </>
  );
};

export default ConditionallyApproveForm;
